/**
 * Progressive loading for the Shop All category shelves.
 *
 * Each shelf arrives with its first page of products. As the shopper scrolls a
 * shelf towards its end, the next page is fetched from the category's own URL
 * through the Section Rendering API (sections/collection-shelf-page) and the
 * slides are appended, until Shopify says there are no pages left.
 *
 * The shelves are not looped: `snippets/collection-category-row` renders a
 * finite carousel, so the theme hides the previous arrow at the start and the
 * next arrow only once everything really has been loaded.
 */

const SECTION_ID = 'collection-shelf-page';

/** How close to the end of a shelf, in px, triggers the next page. */
const LOAD_THRESHOLD = 600;

/**
 * Per-shelf state, keyed off the row element so that a shelf replaced by a
 * filter change (facets.js morphs the whole section) starts clean rather than
 * inheriting the page counter of the shelf it replaced.
 *
 * @type {WeakMap<Element, { page: number, done: boolean, loading: boolean }>}
 */
const shelves = new WeakMap();

/**
 * @param {Element} row
 * @returns {{ page: number, done: boolean, loading: boolean }}
 */
function stateFor(row) {
  let state = shelves.get(row);
  if (!state) {
    // Starts at 0, so the first load re-fetches page 1 — the page the shelf is
    // already showing part of. The shelf's own first page holds however many
    // cards the section setting renders, which need not match this section's
    // page size, so resuming at page 2 could skip whatever sits between the
    // two. Re-fetching and dropping the ids already on screen costs one request
    // and cannot leave a hole, whichever way round the two numbers are.
    state = { page: 0, done: false, loading: false };
    shelves.set(row, state);
  }
  return state;
}

/**
 * The slides the carousel owns — not the ones belonging to the media galleries
 * inside each product card, which are slideshows in their own right.
 *
 * @param {Element} scroller
 * @returns {Element[]}
 */
function ownSlides(scroller) {
  return [...scroller.children].filter((node) => node.tagName === 'SLIDESHOW-SLIDE');
}

/**
 * Each slide drives its reveal animation from a scroll timeline named after its
 * index, and the component lists every name it expects. Appending slides
 * without extending that list leaves them without a timeline, so both ends are
 * renumbered whenever the slide count changes.
 *
 * @param {Element} component
 * @param {Element} scroller
 */
function renumberTimelines(component, scroller) {
  const slides = ownSlides(scroller);
  const names = slides.map((_, index) => `--slide-${index}`);

  for (const [index, slide] of slides.entries()) {
    if (slide instanceof HTMLElement) slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
  }

  if (component instanceof HTMLElement) {
    component.style.setProperty('--slideshow-timeline', names.join(','));
  }
}

/**
 * Builds the Section Rendering URL for a shelf's next page. The shelf carries
 * the category URL with the active filters already on it, so the fetched page
 * is filtered exactly the way the visible page is.
 *
 * @param {string} base
 * @param {number} page
 * @returns {string}
 */
function pageUrl(base, page) {
  const url = new URL(base, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  url.searchParams.set('page', String(page));
  return url.toString();
}

/**
 * Fetches and appends the next page of a shelf.
 *
 * @param {Element} row
 * @param {Element} scroller
 */
async function loadNextPage(row, scroller) {
  const state = stateFor(row);
  const base = row.getAttribute('data-shelf-url');
  if (state.done || state.loading || !base) return;

  const component = scroller.closest('slideshow-component');
  if (!component) return;

  state.loading = true;
  row.setAttribute('data-shelf-loading', '');

  try {
    const response = await fetch(pageUrl(base, state.page + 1));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const page = doc.querySelector('.ccr-shelf-page');
    if (!page) throw new Error('No shelf page in the response');

    // Page 1 of a filtered shelf is assembled from the filtered all-products
    // list, so it can hold products that the category's own page 1 also holds.
    // Skipping ids already on the shelf keeps that from showing up as a
    // duplicate card rather than trying to make the two orderings agree.
    const seen = new Set(
      [...row.querySelectorAll('[data-shelf-product]')].map((card) => card.getAttribute('data-shelf-product'))
    );

    const fresh = [...page.children].filter((slide) => {
      const id = slide.querySelector('[data-shelf-product]')?.getAttribute('data-shelf-product');
      return id != null && !seen.has(id);
    });

    for (const slide of fresh) {
      // Appended slides start hidden the way the server renders every slide
      // after the first; the component's own observer reveals them on scroll.
      slide.setAttribute('aria-hidden', 'true');
      scroller.append(slide);
    }

    if (fresh.length) renumberTimelines(component, scroller);

    state.page += 1;
    state.done = page.getAttribute('data-has-next') !== 'true';

    // A page that was entirely duplicates would otherwise stall the shelf on
    // the same page forever, so keep going while Shopify still has pages.
    if (!fresh.length && !state.done) {
      state.loading = false;
      await loadNextPage(row, scroller);
      return;
    }
  } catch {
    // Leave the shelf with what it already has — it stays scrollable, and the
    // "See all" link is still the complete list.
    state.done = true;
  } finally {
    state.loading = false;
    row.removeAttribute('data-shelf-loading');
  }
}

/**
 * @param {Element} row
 * @param {Element} scroller
 */
function maybeLoadMore(row, scroller) {
  const remaining = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
  if (remaining <= LOAD_THRESHOLD) loadNextPage(row, scroller);
}

// Captured rather than bound per shelf: scroll events do not bubble, and the
// shelves are replaced wholesale whenever a filter changes. Listening at the
// document means a morphed-in shelf works without any re-initialisation.
document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.tagName !== 'SLIDESHOW-SLIDES') return;

    const row = target.closest('.ccr__row');
    if (row) maybeLoadMore(row, target);
  },
  { capture: true, passive: true }
);
